import { generateReportAdmin, getRedemptions } from "apis";
import { updateRedemptionRemarks, voidRedemption } from "apis/redemption.api";
import { Button, Intro } from "components/commons";
import { ConfirmModal, SuccessModal } from "components/modals";
import { UserContext } from "contexts";
import { ErrorCode, Path, ReportType, UserAccess } from "enums";
import { useApi, useModal, useRouter } from "hooks";
import useDataTable from "hooks/useDataTable";
import useExport from "hooks/useExport";
import locale from "localization";
import { useCallback, useContext, useMemo, useState } from "react";
import { formatVolume } from "utils";
import { columns } from "./redemption-columns";
import RedemptionFilter from "./redemption-filter";
import { initialFilterState } from "./redemption-filter.state";
import RedemptionList from "./redemption-list";
import { mapDataToList, mapFilterToRequest } from "./redemption.mapper";
import VoidTransactionModal from "./void-transaction-modal";

const RedemptionModule = () => {
  const { sessionId, userAccess } = useContext(UserContext);

  const getUserAccess = userAccess.find((item) => {
    return item.key === UserAccess.PLCRedemption;
  });

  const { location, history } = useRouter();

  const voidTransactionModal = useModal();
  const confirmModal = useModal();

  const [remarks, setRemarks] = useState("");
  const [value, setValue] = useState("");

  const columnsList = useMemo(() => {
    return getUserAccess?.fullAccess || getUserAccess?.modifyAccess
      ? columns
      : columns.filter((e) => e.key !== "options");
  }, [getUserAccess]);

  const { request: voidRequest, loading: voidingRequest } = useApi({
    api: voidRedemption,
    pageError: false,
    modalError: true,
    handleOwnError: true,
  });

  const updateRemarksApi = useApi({
    api: updateRedemptionRemarks,
    modalError: true,
    pageError: false,
  });

  const onVoidTransactionCb = useCallback(
    async ({ redemptionItem, value }) => {
      try {
        await voidRequest({ redemptionId: redemptionItem.redemptionId, remarks: value });
        voidTransactionModal.close();
        table.fetch({});

        const userDetail = redemptionItem?.userDetail;

        confirmModal.show({
          title: locale.transactionVoided,
          content: (
            <locale.Populate
              text={locale.thisTransactionHasBeenVoided}
              items={[
                <b>{formatVolume(redemptionItem?.literVolume)}</b>,
                <b>{`${userDetail.firstName} ${userDetail.lastName}.`}</b>,
              ]}
            />
          ),
          loading: voidingRequest,
          redemptionItem,
        });
        setRemarks("");
      } catch (err) {
        const errorCode =
          err?.data?.errorCode || err?.data?.code || err?.data?.messageCode || err?.errorCode;

        if (
          errorCode === ErrorCode.TxnVoidingWindowExpired ||
          err?.data?.message?.includes?.(ErrorCode.TxnVoidingWindowExpired)
        ) {
          voidTransactionModal.close();
          confirmModal.show({
            title: locale.unableToVoidTransaction,
            content: locale.voidingWindowExpiredMessage,
            primary: {
              text: locale.gotIt,
              onClick: () => {
                confirmModal.close();
              },
            },
          });
          return;
        }

        return err.showError?.();
      }
    },
    //eslint-disable-next-line
    [voidRequest, voidTransactionModal, voidingRequest, confirmModal],
  );

  const { filter, search, table } = useDataTable({
    api: { api: getRedemptions, params: { platformType: "plc" } },
    filter: { initialState: initialFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "redemptions",
      mapper: (redemptions) =>
        mapDataToList(
          redemptions,
          setRemarks,
          voidTransactionModal,
          onVoidTransactionCb,
          updateRemarksApi,
          setValue,
          getUserAccess,
        ),
      columns: columnsList,
    },
    searchKey: location?.state?.searchKey,
  });

  const exports = useExport({
    api: generateReportAdmin,
    hasModal: true,
    reportType: ReportType.Redemption,
    mappedFilterState: {
      searchKey: search.searchKey,
      platformType: "plc",
      connectionId: sessionId,
      ...filter.mappedFilterState,
    },
  });

  return (
    <div>
      <ConfirmModal {...confirmModal} />
      <SuccessModal {...exports?.successModalComponent} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Intro title={locale.redemptions} subtitle={locale.viewAllTrackRedemptions} />
        {(getUserAccess?.fullAccess || getUserAccess?.modifyAccess) && (
          <Button primary onClick={() => history.push(Path.PLCManualRedemption)}>
            {locale.manualRedemption}
          </Button>
        )}
      </div>
      <RedemptionFilter {...filter} {...search} {...exports} getUserAccess={getUserAccess} />
      <RedemptionList table={table} />
      <VoidTransactionModal
        {...voidTransactionModal}
        remarks={remarks}
        onChangeRemarks={(v) => {
          if (v.length <= 255) {
            setRemarks(v);
          }
        }}
        loading={voidingRequest || updateRemarksApi.loading}
        onSubmit={voidTransactionModal.submit}
        value={value}
        setValue={setValue}
      />
    </div>
  );
};

export default RedemptionModule;
